import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../context/AuthContext';
import { getMacroColor } from '../styles/theme';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const emptyForm = {
  recipe: null,
  storage: 'freezer',
  portionsAvailable: 1,
  expiresAt: '',
  notes: '',
};

async function readJsonResponse(res, fallbackMessage) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch (error) {
    const snippet = text.slice(0, 80).replace(/\s+/g, ' ');
    throw new Error(`${fallbackMessage}. La API devolvió ${res.status} ${res.headers.get('content-type') || ''}: ${snippet}`);
  }
}

function toEditForm(item) {
  return {
    recipe: item.sourceRecipe || null,
    storage: item.storage || 'freezer',
    portionsAvailable: item.portionsAvailable || 1,
    expiresAt: item.expiresAt ? item.expiresAt.split('T')[0] : '',
    notes: item.notes || '',
  };
}

function getRecipeMacros(recipe) {
  return recipe?.totalMacronutrients || recipe?.macros || {};
}

export default function FoodStockPage() {
  const { authToken } = useAuth();
  const [tab, setTab] = useState('freezer');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [recipeSearch, setRecipeSearch] = useState('');
  const [recipeResults, setRecipeResults] = useState([]);
  const [recipeLoading, setRecipeLoading] = useState(false);
  const [useDialog, setUseDialog] = useState({ open: false, item: null, mode: 'use', portions: 1 });
  const searchTimeout = useRef(null);

  const headers = useMemo(() => ({
    Authorization: `Bearer ${authToken}`,
    'Content-Type': 'application/json',
  }), [authToken]);

  const loadStock = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/food-stock?storage=${tab}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const wrapper = await readJsonResponse(res, 'Error cargando fondo');
      if (!res.ok) throw new Error(wrapper.message || 'Error cargando fondo');
      setItems(wrapper.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authToken, tab]);

  useEffect(() => {
    loadStock();
  }, [loadStock]);

  useEffect(() => {
    if (!formOpen || editingItem || recipeSearch.trim().length < 2) {
      setRecipeResults([]);
      return undefined;
    }

    setRecipeLoading(true);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/recipes/search?name=${encodeURIComponent(recipeSearch.trim())}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const data = await readJsonResponse(res, 'Error buscando recetas');
        setRecipeResults(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message);
        setRecipeResults([]);
      } finally {
        setRecipeLoading(false);
      }
    }, 300);

    return () => clearTimeout(searchTimeout.current);
  }, [authToken, editingItem, formOpen, recipeSearch]);

  const openCreate = () => {
    setEditingItem(null);
    setForm({ ...emptyForm, storage: tab });
    setRecipeSearch('');
    setRecipeResults([]);
    setFormOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm(toEditForm(item));
    setRecipeSearch(item.sourceRecipe?.name || item.name || '');
    setRecipeResults([]);
    setFormOpen(true);
  };

  const saveForm = async () => {
    const body = {
      storage: form.storage,
      portionsAvailable: Number(form.portionsAvailable) || 0,
      portionUnit: 'raciones',
      expiresAt: form.storage === 'freezer' ? undefined : form.expiresAt || undefined,
      notes: form.notes,
    };

    if (!editingItem) {
      body.sourceRecipe = form.recipe?._id;
      body.name = form.recipe?.name;
      body.sourceType = 'recipe';
    }

    const url = editingItem
      ? `${API_URL}/food-stock/${editingItem._id}`
      : `${API_URL}/food-stock`;
    const method = editingItem ? 'PUT' : 'POST';

    try {
      const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
      const wrapper = await readJsonResponse(res, 'Error guardando fondo');
      if (!res.ok) throw new Error(wrapper.message || 'Error guardando fondo');
      setFormOpen(false);
      await loadStock();
    } catch (err) {
      setError(err.message);
    }
  };

  const applyStockAction = async () => {
    const { item, mode } = useDialog;
    const portions = Number(useDialog.portions) || 0;
    if (!item || portions <= 0) return;

    const endpoint = mode === 'discard' ? 'discard' : 'use';
    try {
      const res = await fetch(`${API_URL}/food-stock/${item._id}/${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ portions, reason: mode === 'use' ? 'manual' : undefined }),
      });
      const wrapper = await readJsonResponse(res, 'Error actualizando fondo');
      if (!res.ok) throw new Error(wrapper.message || 'Error actualizando fondo');
      setUseDialog({ open: false, item: null, mode: 'use', portions: 1 });
      await loadStock();
    } catch (err) {
      setError(err.message);
    }
  };

  const storageLabel = tab === 'freezer' ? 'Congelador' : 'Nevera';
  const selectedMacros = getRecipeMacros(form.recipe);

  return (
    <Container className="modern-resource-page" maxWidth="xl" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} gap={2} flexWrap="wrap">
        <Box>
          <Typography variant="h4" fontWeight="800" color="#1e293b" gutterBottom>
            Fondo
          </Typography>
          <Typography color="text.secondary">
            Raciones preparadas de recetas existentes para planificar sin volver a comprar ingredientes.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ bgcolor: '#4f46e5' }}>
          Añadir receta
        </Button>
      </Box>

      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 3 }}>
        <Tab value="freezer" icon={<AcUnitIcon />} iconPosition="start" label="Congelador" />
        <Tab value="fridge" icon={<Inventory2Icon />} iconPosition="start" label="Nevera" />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {tab === 'freezer' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          El fondo de congelador no caduca en Nutrizia. Usa notas si quieres apuntar fecha de preparación o lote.
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>
      ) : items.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" fontWeight="700" gutterBottom>
              No hay recetas en {storageLabel.toLowerCase()}
            </Typography>
            <Typography color="text.secondary" mb={3}>
              Añade una receta existente y cuántas raciones tienes preparadas.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ bgcolor: '#4f46e5' }}>
              Añadir receta al fondo
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {items.map(item => {
            const macros = item.macrosPerPortion || item.sourceRecipe?.totalMacronutrients || {};
            const isFreezer = item.storage === 'freezer';
            const recipeName = item.sourceRecipe?.name || item.name;
            return (
              <Grid item xs={12} sm={6} lg={4} key={item._id}>
                <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                      <Box minWidth={0}>
                        <Typography variant="h6" fontWeight="800" color="#1e293b" noWrap>
                          {recipeName}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
                          <Chip
                            size="small"
                            icon={isFreezer ? <AcUnitIcon /> : <Inventory2Icon />}
                            label={isFreezer ? 'Congelador' : 'Nevera'}
                          />
                          <Chip size="small" label={`${item.portionsAvailable} raciones`} />
                        </Stack>
                      </Box>
                      <Box display="flex">
                        <IconButton size="small" onClick={() => openEdit(item)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setUseDialog({ open: true, item, mode: 'discard', portions: item.portionsAvailable || 1 })}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    <Typography variant="body2" color="text.secondary" mt={2}>
                      {isFreezer ? 'Sin caducidad' : item.expiresInDays != null ? `Caduca en ${item.expiresInDays} días` : 'Sin fecha de caducidad'}
                    </Typography>

                    <Box display="flex" gap={1.5} mt={2} flexWrap="wrap">
                      <Typography variant="caption" fontWeight="800" color={getMacroColor('kilocalories')}>
                        {Math.round(macros.kilocalories || 0)} kcal/ración
                      </Typography>
                      <Typography variant="caption" color={getMacroColor('protein')}>
                        P {Math.round(macros.protein || 0)}g
                      </Typography>
                      <Typography variant="caption" color={getMacroColor('carbs')}>
                        C {Math.round(macros.carbs || 0)}g
                      </Typography>
                      <Typography variant="caption" color={getMacroColor('fat')}>
                        G {Math.round(macros.fat || 0)}g
                      </Typography>
                    </Box>

                    <Box display="flex" gap={1} mt={3}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<RemoveCircleOutlineIcon />}
                        onClick={() => setUseDialog({ open: true, item, mode: 'use', portions: 1 })}
                      >
                        Usar
                      </Button>
                      {item.sourceRecipe?._id && (
                        <Button
                          fullWidth
                          variant="text"
                          startIcon={<RestaurantIcon />}
                          href={`/recetas/${item.sourceRecipe._id}`}
                        >
                          Receta
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingItem ? 'Editar raciones de fondo' : 'Añadir receta al fondo'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {!editingItem ? (
              <Box>
                <TextField
                  label="Buscar receta"
                  value={recipeSearch}
                  onChange={e => {
                    setRecipeSearch(e.target.value);
                    setForm({ ...form, recipe: null });
                  }}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
                  }}
                />
                {recipeLoading && <Box display="flex" justifyContent="center" py={2}><CircularProgress size={22} /></Box>}
                {!recipeLoading && recipeResults.length > 0 && (
                  <List dense sx={{ mt: 1, border: '1px solid #e2e8f0', borderRadius: 2, maxHeight: 260, overflow: 'auto' }}>
                    {recipeResults.map(recipe => {
                      const macros = getRecipeMacros(recipe);
                      const selected = form.recipe?._id === recipe._id;
                      return (
                        <ListItem
                          key={recipe._id}
                          button
                          selected={selected}
                          onClick={() => {
                            setForm({ ...form, recipe });
                            setRecipeSearch(recipe.name);
                            setRecipeResults([]);
                          }}
                        >
                          <ListItemAvatar>
                            <Box sx={{ width: 52, aspectRatio: '4 / 3', borderRadius: '8px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, bgcolor: '#eef2ff', color: '#475569', fontWeight: 700 }}>
                              {recipe.mainImage ? (
                                <Box component="img" src={recipe.mainImage} alt={recipe.name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                recipe.name?.[0] || 'R'
                              )}
                            </Box>
                          </ListItemAvatar>
                          <ListItemText
                            primary={recipe.name}
                            secondary={`${Math.round(macros.kilocalories || 0)} kcal/ración`}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Box>
            ) : (
              <Alert severity="info">
                {form.recipe?.name || editingItem?.name}
              </Alert>
            )}

            {form.recipe && (
              <Box display="flex" gap={1.5} flexWrap="wrap">
                <Chip size="small" label={`${Math.round(selectedMacros.kilocalories || 0)} kcal/ración`} />
                <Chip size="small" label={`P ${Math.round(selectedMacros.protein || 0)}g`} />
                <Chip size="small" label={`C ${Math.round(selectedMacros.carbs || 0)}g`} />
                <Chip size="small" label={`G ${Math.round(selectedMacros.fat || 0)}g`} />
              </Box>
            )}

            <Tabs value={form.storage} onChange={(_, value) => setForm({ ...form, storage: value, expiresAt: value === 'freezer' ? '' : form.expiresAt })}>
              <Tab value="freezer" icon={<AcUnitIcon />} iconPosition="start" label="Congelador" />
              <Tab value="fridge" icon={<Inventory2Icon />} iconPosition="start" label="Nevera" />
            </Tabs>
            <TextField label="Raciones disponibles" type="number" value={form.portionsAvailable} onChange={e => setForm({ ...form, portionsAvailable: e.target.value })} fullWidth />
            {form.storage === 'fridge' && (
              <TextField label="Caducidad" type="date" value={form.expiresAt} onChange={e => setForm({ ...form, expiresAt: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
            )}
            <TextField label="Notas" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} fullWidth multiline minRows={2} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancelar</Button>
          <Button onClick={saveForm} variant="contained" disabled={(!editingItem && !form.recipe) || Number(form.portionsAvailable) <= 0}>Guardar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={useDialog.open} onClose={() => setUseDialog({ open: false, item: null, mode: 'use', portions: 1 })} fullWidth maxWidth="xs">
        <DialogTitle>{useDialog.mode === 'discard' ? 'Descartar raciones' : 'Usar raciones'}</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" mb={2}>
            {useDialog.item?.sourceRecipe?.name || useDialog.item?.name}
          </Typography>
          <TextField
            autoFocus
            label="Raciones"
            type="number"
            value={useDialog.portions}
            inputProps={{ min: 0, max: useDialog.item?.portionsAvailable || 1, step: 0.5 }}
            onChange={e => setUseDialog({ ...useDialog, portions: e.target.value })}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUseDialog({ open: false, item: null, mode: 'use', portions: 1 })}>Cancelar</Button>
          <Button onClick={applyStockAction} variant="contained">
            {useDialog.mode === 'discard' ? 'Descartar' : 'Usar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
